import {
  collection,
  collectionGroup,
  doc,
  getDoc,
  getDocs
} from 'firebase/firestore'
import CHAMPIONSHIPS from '../data/championships'
import { getMatchTitleValues } from './matchTitles'
import {
  buildTitleStatsByTitle,
  extractShowIdFromRefPath,
  TITLE_DEFENSE_BONUS_VALUES,
  TITLE_REIGN_MILESTONE_BONUS_DAYS,
  TITLE_WIN_BONUS_VALUES
} from './titleStats'

const DAY_IN_MS = 24 * 60 * 60 * 1000

export const TITLE_REIGN_BONUS_WINDOWS = [
  {
    bonusValue: 'campione_100',
    bonusLabel: 'Campione da 100 giorni',
    targetDays: 100,
    minDays: 93,
    maxDays: 107,
    source: 'reign'
  },
  {
    bonusValue: 'campione_200',
    bonusLabel: 'Campione da 200 giorni',
    targetDays: 200,
    minDays: 193,
    maxDays: 207,
    source: 'reign'
  },
  {
    bonusValue: 'campione_280',
    bonusLabel: 'Campione da 280 giorni',
    targetDays: 280,
    minDays: 273,
    maxDays: 287,
    source: 'reign'
  },
  {
    bonusValue: 'campione_365',
    bonusLabel: 'Campione da 365 giorni',
    targetDays: 365,
    minDays: 358,
    maxDays: 372,
    source: 'reign'
  }
]

export const TITLE_DEFENSE_DROUGHT_WINDOW = {
  bonusValue: 'no_difesa_3mesi',
  bonusLabel: 'Non difende il titolo per 3 mesi',
  targetDays: 90,
  minDays: 83,
  maxDays: 97,
  source: 'defense'
}

export const TITLE_SHORT_REIGN_BONUSES = [
  {
    bonusValue: 'campione_meno_10',
    bonusLabel: 'Campione < 10 giorni',
    thresholdDays: 10
  },
  {
    bonusValue: 'campione_meno_40',
    bonusLabel: 'Campione < 40 giorni',
    thresholdDays: 40
  }
]

const TRACKED_SUGGESTION_BONUSES = new Set([
  ...TITLE_WIN_BONUS_VALUES,
  ...TITLE_DEFENSE_BONUS_VALUES,
  ...TITLE_REIGN_MILESTONE_BONUS_DAYS.keys(),
  TITLE_DEFENSE_DROUGHT_WINDOW.bonusValue,
  ...TITLE_SHORT_REIGN_BONUSES.map(bonus => bonus.bonusValue)
])

const TITLE_NAMES_BY_VALUE = new Map(
  CHAMPIONSHIPS.map(championship => [championship.value, championship.name])
)

const toDateValue = value => {
  if (!value) return null
  if (value instanceof Date) return value
  if (typeof value?.toDate === 'function') return value.toDate()
  if (typeof value?.seconds === 'number') {
    return new Date(value.seconds * 1000)
  }

  const parsed = new Date(value)
  return Number.isNaN(parsed.getTime()) ? null : parsed
}

const getShowDateValue = showData =>
  toDateValue(showData?.data || showData?.date || showData?.timestamp)

const normalizeStoredBonusValue = value =>
  String(value || '').replace(/_ext$/, '')

const getMatchParticipantIds = match => {
  const participantIds = Array.isArray(match?.participants)
    ? match.participants
    : []
  const teamParticipantIds = Array.isArray(match?.teams)
    ? match.teams.flatMap(team =>
        Array.isArray(team?.participants) ? team.participants : []
      )
    : []

  return [...new Set([...participantIds, ...teamParticipantIds].filter(Boolean))]
}

const getTitleName = titleValue => TITLE_NAMES_BY_VALUE.get(titleValue) || titleValue

const addDays = (dateValue, daysToAdd) => {
  if (!(dateValue instanceof Date) || Number.isNaN(dateValue.getTime())) return null

  return new Date(
    dateValue.getTime() + Number(daysToAdd || 0) * DAY_IN_MS
  )
}

const isCountInWindow = (count, windowDefinition) =>
  Number.isFinite(count) &&
  count >= windowDefinition.minDays &&
  count <= windowDefinition.maxDays

const getAbsoluteTimeDistance = (firstDate, secondDate) =>
  Math.abs(firstDate.getTime() - secondDate.getTime())

const sortShowsByTargetDateDistance = (shows, targetDate) =>
  [...shows].sort((firstShow, secondShow) => {
    const firstDistance = getAbsoluteTimeDistance(firstShow.dateValue, targetDate)
    const secondDistance = getAbsoluteTimeDistance(secondShow.dateValue, targetDate)

    if (firstDistance !== secondDistance) {
      return firstDistance - secondDistance
    }

    const firstOnOrAfter = firstShow.dateValue.getTime() >= targetDate.getTime() ? 1 : 0
    const secondOnOrAfter = secondShow.dateValue.getTime() >= targetDate.getTime() ? 1 : 0

    if (firstOnOrAfter !== secondOnOrAfter) {
      return secondOnOrAfter - firstOnOrAfter
    }

    return firstShow.dateValue.getTime() - secondShow.dateValue.getTime()
  })

const getWindowDateRange = (referenceDate, windowDefinition) => {
  if (!(referenceDate instanceof Date) || Number.isNaN(referenceDate.getTime())) {
    return {
      minDate: null,
      targetDate: null,
      maxDate: null
    }
  }

  return {
    minDate: addDays(referenceDate, windowDefinition.minDays),
    targetDate: addDays(referenceDate, windowDefinition.targetDays),
    maxDate: addDays(referenceDate, windowDefinition.maxDays)
  }
}

const getShowsInsideDateRange = (shows, minDate, maxDate) =>
  shows.filter(show => {
    const showDate = show?.dateValue
    if (!(showDate instanceof Date) || Number.isNaN(showDate.getTime())) return false
    return showDate >= minDate && showDate <= maxDate
  })

const getClosestShowToTargetDate = ({
  shows = [],
  targetDate,
  minDate = null,
  maxDate = null
}) => {
  if (!(targetDate instanceof Date) || Number.isNaN(targetDate.getTime())) return null

  const validShows = shows.filter(
    show =>
      show?.dateValue instanceof Date && !Number.isNaN(show.dateValue.getTime())
  )

  if (validShows.length === 0) return null

  const windowShows =
    minDate instanceof Date &&
    maxDate instanceof Date &&
    !Number.isNaN(minDate.getTime()) &&
    !Number.isNaN(maxDate.getTime())
      ? getShowsInsideDateRange(validShows, minDate, maxDate)
      : []

  const candidateShows = windowShows.length > 0 ? windowShows : validShows
  return sortShowsByTargetDateDistance(candidateShows, targetDate)[0] || null
}

const getClosestPastShow = (shows = [], referenceDate = new Date()) => {
  if (!(referenceDate instanceof Date) || Number.isNaN(referenceDate.getTime())) {
    return null
  }

  const pastShows = shows
    .filter(
      show =>
        show?.dateValue instanceof Date &&
        !Number.isNaN(show.dateValue.getTime()) &&
        show.dateValue.getTime() <= referenceDate.getTime()
    )
    .sort((firstShow, secondShow) => secondShow.dateValue - firstShow.dateValue)

  return pastShows[0] || null
}

const getParticipationRelatedTitleValues = ({
  wrestlerId,
  titleValueSet,
  titleValues,
  showId,
  matchesByShowId
}) => {
  const relatedTitleValues = [
    ...new Set(
      (matchesByShowId.get(showId) || [])
        .filter(match => getMatchParticipantIds(match).includes(wrestlerId))
        .flatMap(getMatchTitleValues)
        .filter(titleValue => titleValueSet.has(titleValue))
    )
  ]

  if (relatedTitleValues.length > 0) {
    return relatedTitleValues
  }

  return titleValues.length === 1 ? titleValues : []
}

const hasRelevantSuggestionBonus = participation => {
  const bonuses = Array.isArray(participation?.bonuses) ? participation.bonuses : []

  return bonuses.some(bonus =>
    TRACKED_SUGGESTION_BONUSES.has(normalizeStoredBonusValue(bonus))
  )
}

export const isChampionDaysInBonusWindow = championDays =>
  TITLE_REIGN_BONUS_WINDOWS.some(windowDefinition =>
    isCountInWindow(championDays, windowDefinition)
  )

export const buildTitleBonusHistoryByTitle = ({
  wrestlerId,
  titleValues = [],
  participations = [],
  matchesByShowId = new Map(),
  showDatesByShowId = new Map()
}) => {
  const normalizedTitleValues = [...new Set(titleValues.filter(Boolean))]
  const titleValueSet = new Set(normalizedTitleValues)
  const historyByTitle = normalizedTitleValues.reduce((acc, titleValue) => {
    acc[titleValue] = []
    return acc
  }, {})

  participations.forEach(participation => {
    const normalizedBonuses = Array.isArray(participation?.bonuses)
      ? participation.bonuses.map(normalizeStoredBonusValue)
      : []
    const relevantBonuses = normalizedBonuses.filter(bonusValue =>
      TRACKED_SUGGESTION_BONUSES.has(bonusValue)
    )

    if (relevantBonuses.length === 0) return

    const showId = extractShowIdFromRefPath(participation?.refPath)
    if (!showId) return

    const showDate = showDatesByShowId.get(showId)
    if (!(showDate instanceof Date) || Number.isNaN(showDate.getTime())) return

    const relatedTitleValues = getParticipationRelatedTitleValues({
      wrestlerId,
      titleValueSet,
      titleValues: normalizedTitleValues,
      showId,
      matchesByShowId
    })

    if (relatedTitleValues.length === 0) return

    relatedTitleValues.forEach(titleValue => {
      relevantBonuses.forEach(bonusValue => {
        historyByTitle[titleValue].push({
          bonusValue,
          showId,
          showDate
        })
      })
    })
  })

  Object.values(historyByTitle).forEach(entries => {
    entries.sort((firstEntry, secondEntry) => firstEntry.showDate - secondEntry.showDate)
  })

  return historyByTitle
}

const hasBonusAssignedSinceDate = ({
  history = [],
  bonusValue,
  sinceDate
}) => {
  if (!(sinceDate instanceof Date) || Number.isNaN(sinceDate.getTime())) return false

  return history.some(
    entry =>
      entry?.bonusValue === bonusValue &&
      entry?.showDate instanceof Date &&
      !Number.isNaN(entry.showDate.getTime()) &&
      entry.showDate.getTime() >= sinceDate.getTime()
  )
}

const buildTitleSuggestionEntry = ({
  wrestler,
  titleValue,
  bonusValue,
  bonusLabel,
  titleStat,
  windowDefinition,
  referenceDate,
  currentCount,
  shows,
  currentShowId = ''
}) => {
  const { minDate, targetDate, maxDate } = getWindowDateRange(
    referenceDate,
    windowDefinition
  )
  const suggestedShow = getClosestShowToTargetDate({
    shows,
    targetDate,
    minDate,
    maxDate
  })

  return {
    id: `${wrestler.id}:${titleValue}:${bonusValue}`,
    wrestlerId: wrestler.id,
    wrestlerName: wrestler.name || 'Sconosciuto',
    titleValue,
    titleName: getTitleName(titleValue),
    bonusValue,
    bonusLabel,
    currentCount,
    targetDays: windowDefinition.targetDays,
    minDays: windowDefinition.minDays,
    maxDays: windowDefinition.maxDays,
    referenceDate,
    targetDate,
    suggestedShow,
    suggestedShowId: suggestedShow?.id || '',
    isCurrentShowSuggestion: Boolean(
      currentShowId && suggestedShow?.id === currentShowId
    ),
    source: windowDefinition.source,
    daysAsChampion: titleStat?.daysAsChampion || 0,
    daysSinceLastDefense: titleStat?.daysSinceLastDefense || 0
  }
}

export const buildTitleBonusSuggestionsForWrestler = ({
  wrestler,
  titleStatsByTitle = {},
  bonusHistoryByTitle = {},
  shows = [],
  currentShowId = ''
}) => {
  if (!wrestler?.id) return []

  const titleValues = Array.isArray(wrestler.championTypes)
    ? wrestler.championTypes.filter(Boolean)
    : []

  const suggestions = []

  titleValues.forEach(titleValue => {
    const titleStat = titleStatsByTitle[titleValue]
    if (!titleStat?.hasData) return

    const titleHistory = Array.isArray(bonusHistoryByTitle[titleValue])
      ? bonusHistoryByTitle[titleValue]
      : []

    TITLE_REIGN_BONUS_WINDOWS.forEach(windowDefinition => {
      if (!titleStat.wonAt) return
      if (!isCountInWindow(titleStat.daysAsChampion, windowDefinition)) return
      if (
        hasBonusAssignedSinceDate({
          history: titleHistory,
          bonusValue: windowDefinition.bonusValue,
          sinceDate: titleStat.wonAt
        })
      ) {
        return
      }

      suggestions.push(
        buildTitleSuggestionEntry({
          wrestler,
          titleValue,
          bonusValue: windowDefinition.bonusValue,
          bonusLabel: windowDefinition.bonusLabel,
          titleStat,
          windowDefinition,
          referenceDate: titleStat.wonAt,
          currentCount: titleStat.daysAsChampion,
          shows,
          currentShowId
        })
      )
    })

    if (
      titleStat.defendedAt &&
      isCountInWindow(
        titleStat.daysSinceLastDefense,
        TITLE_DEFENSE_DROUGHT_WINDOW
      ) &&
      !hasBonusAssignedSinceDate({
        history: titleHistory,
        bonusValue: TITLE_DEFENSE_DROUGHT_WINDOW.bonusValue,
        sinceDate: titleStat.defendedAt
      })
    ) {
      suggestions.push(
        buildTitleSuggestionEntry({
          wrestler,
          titleValue,
          bonusValue: TITLE_DEFENSE_DROUGHT_WINDOW.bonusValue,
          bonusLabel: TITLE_DEFENSE_DROUGHT_WINDOW.bonusLabel,
          titleStat,
          windowDefinition: TITLE_DEFENSE_DROUGHT_WINDOW,
          referenceDate: titleStat.defendedAt,
          currentCount: titleStat.daysSinceLastDefense,
          shows,
          currentShowId
        })
      )
    }
  })

  return suggestions.sort((firstSuggestion, secondSuggestion) => {
    const firstCurrent = firstSuggestion.isCurrentShowSuggestion ? 1 : 0
    const secondCurrent = secondSuggestion.isCurrentShowSuggestion ? 1 : 0

    if (firstCurrent !== secondCurrent) {
      return secondCurrent - firstCurrent
    }

    const firstTime = firstSuggestion.targetDate?.getTime() || Number.MAX_SAFE_INTEGER
    const secondTime = secondSuggestion.targetDate?.getTime() || Number.MAX_SAFE_INTEGER

    if (firstTime !== secondTime) {
      return firstTime - secondTime
    }

    return firstSuggestion.bonusLabel.localeCompare(
      secondSuggestion.bonusLabel,
      'it'
    )
  })
}

export const buildTitleChangePenaltySuggestions = ({
  titleValue = '',
  currentChampions = [],
  titleStatsByWrestler = {},
  bonusHistoryByWrestler = {},
  shows = [],
  referenceDate = new Date()
}) => {
  const latestPastShow = getClosestPastShow(shows, referenceDate)

  return currentChampions
    .flatMap(champion => {
      const titleStat = titleStatsByWrestler[champion.id]?.[titleValue]
      if (!titleStat?.wonAt || !titleStat.daysAsChampion) return []

      const penaltyDefinition =
        titleStat.daysAsChampion < TITLE_SHORT_REIGN_BONUSES[0].thresholdDays
          ? TITLE_SHORT_REIGN_BONUSES[0]
          : titleStat.daysAsChampion < TITLE_SHORT_REIGN_BONUSES[1].thresholdDays
          ? TITLE_SHORT_REIGN_BONUSES[1]
          : null

      if (!penaltyDefinition) return []

      const titleHistory = Array.isArray(
        bonusHistoryByWrestler[champion.id]?.[titleValue]
      )
        ? bonusHistoryByWrestler[champion.id][titleValue]
        : []

      if (
        hasBonusAssignedSinceDate({
          history: titleHistory,
          bonusValue: penaltyDefinition.bonusValue,
          sinceDate: titleStat.wonAt
        })
      ) {
        return []
      }

      return [
        {
          id: `${champion.id}:${titleValue}:${penaltyDefinition.bonusValue}`,
          wrestlerId: champion.id,
          wrestlerName: champion.name || 'Sconosciuto',
          titleValue,
          titleName: getTitleName(titleValue),
          bonusValue: penaltyDefinition.bonusValue,
          bonusLabel: penaltyDefinition.bonusLabel,
          daysAsChampion: titleStat.daysAsChampion,
          suggestedShow: latestPastShow,
          suggestedShowId: latestPastShow?.id || ''
        }
      ]
    })
    .sort((firstSuggestion, secondSuggestion) =>
      firstSuggestion.wrestlerName.localeCompare(
        secondSuggestion.wrestlerName,
        'it'
      )
    )
}

export const formatSuggestedShowLabel = show => {
  if (!show?.dateValue) return 'Show non disponibile'

  const dateLabel = show.dateValue.toLocaleDateString('it-IT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  })

  if (show.name) {
    return `${show.name} • ${dateLabel}`
  }

  return dateLabel
}

export const loadShowSchedule = async db => {
  const showsSnap = await getDocs(collection(db, 'shows'))

  return showsSnap.docs
    .map(showDoc => {
      const showData = showDoc.data()
      const dateValue = getShowDateValue(showData)

      return {
        id: showDoc.id,
        name: showData.nome || '',
        type: showData.type || 'weekly',
        data: showData,
        dateValue
      }
    })
    .filter(
      show =>
        show.dateValue instanceof Date && !Number.isNaN(show.dateValue.getTime())
    )
    .sort((firstShow, secondShow) => firstShow.dateValue - secondShow.dateValue)
}

export const loadTitleBonusSuggestionBundle = async ({
  db,
  wrestlerIds = [],
  titleValuesByWrestler = {},
  referenceDate = new Date()
}) => {
  const normalizedWrestlerIds = [...new Set(wrestlerIds.filter(Boolean))]

  if (normalizedWrestlerIds.length === 0) {
    return {
      titleStatsByWrestler: {},
      bonusHistoryByWrestler: {},
      shows: []
    }
  }

  const wrestlerIdSet = new Set(normalizedWrestlerIds)
  const [allParticipationsSnap, shows] = await Promise.all([
    getDocs(collectionGroup(db, 'participations')),
    loadShowSchedule(db)
  ])

  const participationsByWrestler = normalizedWrestlerIds.reduce((acc, wrestlerId) => {
    acc[wrestlerId] = []
    return acc
  }, {})

  allParticipationsSnap.docs.forEach(participationDoc => {
    const participation = {
      id: participationDoc.id,
      ...participationDoc.data(),
      refPath: participationDoc.ref.path
    }

    if (!wrestlerIdSet.has(participation.wrestlerId)) return
    if (!hasRelevantSuggestionBonus(participation)) return

    participationsByWrestler[participation.wrestlerId].push(participation)
  })

  const showIds = [
    ...new Set(
      Object.values(participationsByWrestler).flatMap(participations =>
        participations
          .map(participation => extractShowIdFromRefPath(participation.refPath))
          .filter(Boolean)
      )
    )
  ]

  const showEntries = await Promise.all(
    showIds.map(async showId => {
      const [showSnap, matchesSnap] = await Promise.all([
        getDoc(doc(db, 'shows', showId)),
        getDocs(collection(db, 'shows', showId, 'matches'))
      ])

      return {
        showId,
        showData: showSnap.exists() ? showSnap.data() : {},
        matches: matchesSnap.docs.map(matchDoc => ({
          id: matchDoc.id,
          ...matchDoc.data()
        }))
      }
    })
  )

  const showDatesByShowId = new Map(
    showEntries.map(entry => [entry.showId, getShowDateValue(entry.showData)])
  )
  const matchesByShowId = new Map(
    showEntries.map(entry => [entry.showId, entry.matches])
  )

  const titleStatsByWrestler = normalizedWrestlerIds.reduce((acc, wrestlerId) => {
    acc[wrestlerId] = buildTitleStatsByTitle({
      wrestlerId,
      titleValues: titleValuesByWrestler[wrestlerId] || [],
      participations: participationsByWrestler[wrestlerId] || [],
      matchesByShowId,
      showDatesByShowId,
      now: referenceDate
    })
    return acc
  }, {})

  const bonusHistoryByWrestler = normalizedWrestlerIds.reduce((acc, wrestlerId) => {
    acc[wrestlerId] = buildTitleBonusHistoryByTitle({
      wrestlerId,
      titleValues: titleValuesByWrestler[wrestlerId] || [],
      participations: participationsByWrestler[wrestlerId] || [],
      matchesByShowId,
      showDatesByShowId
    })
    return acc
  }, {})

  return {
    titleStatsByWrestler,
    bonusHistoryByWrestler,
    shows
  }
}
